// Quote-time layout for ducted (compatibility) systems: the pieces the
// customer's quote page needs to draw a layout diagram, manufactured at the
// moment the quote is saved — from the System Design the system's pairs were
// sized on when it still exists, else from the synthetic ducted configuration
// (utils/ducted-layout-configuration).
import {
  ConfigurationType,
  ExistingSystemType,
  LevelType,
  SelectedSystemType,
  SystemOptionConfigurationType,
  SystemOptionType,
  ZoneItemType
} from 'data/types';
import { COMPATIBILITY_MATCH_TAG } from 'utils/compatibility-systems';
import { buildDuctedDesignLayoutConfiguration } from 'utils/ducted-design-layout';
import {
  buildDuctedLayoutConfiguration,
  resolveLayoutExistingSystem
} from 'utils/ducted-layout-configuration';
import {
  buildQuoteFloorplanSnapshot,
  QuoteFloorplanSnapshot
} from 'utils/quote-floorplan-snapshot';
import { ZoneDuctModesMapType } from 'utils/zone-duct-modes';

export type DuctedQuoteLayout = {
  floorplanSnapshot: QuoteFloorplanSnapshot;
  configuration: SystemOptionConfigurationType;
};

export type ApplyDuctedQuoteLayoutParams = {
  system: SelectedSystemType;
  existingSystems: ExistingSystemType[];
  structure: LevelType[];
  configurations?: ConfigurationType[];
  zoneDuctModes?: ZoneDuctModesMapType;
};

// The zone items ride into the persisted quote so the customer page can
// outline the zone, but with zeroed loads: its Required Capacity / Size Match
// read the zone items' BTUs first and only fall back to ductedCoverage's
// requirement when they carry none — and the design loads the coverage was
// computed against must keep winning over a plain room-load sum.
const UNLOADED_BTUS = { heating: 0, cooling: 0 };

function toUnloadedZoneItem(item: ZoneItemType): ZoneItemType {
  return { ...item, BTUs: UNLOADED_BTUS, originalBTUs: UNLOADED_BTUS };
}

export function isDuctedCompatibilitySystem(
  system: Pick<SelectedSystemType, 'tags'>
): boolean {
  return (system.tags ?? []).includes(COMPATIBILITY_MATCH_TAG);
}

export type ResolveDuctedLayoutConfigurationParams = {
  system: SystemOptionType;
  existingSystems: ExistingSystemType[];
  structure: LevelType[];
  configurations?: ConfigurationType[];
};

export function resolveDuctedLayoutConfiguration({
  system,
  existingSystems,
  structure,
  configurations = []
}: ResolveDuctedLayoutConfigurationParams) {
  return (
    buildDuctedDesignLayoutConfiguration({ system, configurations }) ??
    buildDuctedLayoutConfiguration({
      system,
      existingSystem: resolveLayoutExistingSystem(system, existingSystems),
      structure,
      existingSystems
    })
  );
}

export function applyDuctedQuoteLayout({
  system,
  existingSystems,
  structure,
  configurations,
  zoneDuctModes
}: ApplyDuctedQuoteLayoutParams): DuctedQuoteLayout | null {
  if (!isDuctedCompatibilitySystem(system)) return null;

  const layout = resolveDuctedLayoutConfiguration({
    system,
    existingSystems,
    structure,
    configurations
  });
  if (!layout) return null;

  const floorplanSnapshot = buildQuoteFloorplanSnapshot(
    structure,
    layout,
    zoneDuctModes
  );

  const layoutZoneItemsByZoneId = new Map(
    layout.condensers.flatMap((condenser) =>
      condenser.zones.map((zone) => [zone.uniqueId, zone.zoneItems] as const)
    )
  );

  const configuration: SystemOptionConfigurationType = {
    ...system.configuration,
    condensers: system.configuration.condensers.map((condenser) => ({
      ...condenser,
      zones: condenser.zones.map((zone) => ({
        ...zone,
        zoneItems: (
          layoutZoneItemsByZoneId.get(zone.uniqueId) ?? zone.zoneItems
        ).map(toUnloadedZoneItem)
      }))
    }))
  };

  return { floorplanSnapshot, configuration };
}
